import { useEffect, useState } from "react";
import type { AccountUser, TenantRow } from "@/types/account";

function genPassword(): string {
  const chars = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%";
  let out = "";
  for (let i = 0; i < 14; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

type Props = {
  open: boolean;
  edit?: AccountUser;
  tenants: TenantRow[];
  onClose: () => void;
  onSave: (payload: { email: string; role: string; password?: string; allowedSites: string[] }, isEdit: boolean) => Promise<void>;
};

export default function UserModal({ open, edit, tenants, onClose, onSave }: Props) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  const [password, setPassword] = useState("");
  const [allowedSites, setAllowedSites] = useState<string[]>([]);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmail(String(edit?.email || ""));
    setRole(String(edit?.role || "user"));
    setPassword("");
    setAllowedSites(Array.isArray(edit?.allowedSites) ? [...edit!.allowedSites!] : []);
    setErr("");
  }, [open, edit]);

  if (!open) return null;

  const isEdit = Boolean(edit?.email);
  const domains = tenants.map((t) => String(t.domain || "")).filter(Boolean);

  const toggleSite = (d: string) => {
    setAllowedSites((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  };

  const handleSave = async () => {
    const em = email.trim();
    if (!em) {
      setErr("Email is required.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      await onSave(
        { email: em, role, ...(password ? { password } : {}), allowedSites },
        isEdit
      );
    } catch (e) {
      setErr(String(e instanceof Error ? e.message : e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modalBackdrop open" aria-hidden="false" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modalHead">
          <div>
            <div className="modalTitle">{isEdit ? "Edit user" : "New user"}</div>
            <div className="modalSub">Create or edit a dashboard user</div>
          </div>
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </div>
        <div className="modalBody">
          <div className="row">
            <div className="field" style={{ flex: 1, minWidth: 240 }}>
              <label>Email</label>
              <input type="email" value={email} disabled={isEdit} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" />
            </div>
            <div className="field" style={{ width: 200 }}>
              <label>Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="user">User</option>
                <option value="administrator">Administrator</option>
              </select>
            </div>
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <div className="field" style={{ flex: 1, minWidth: 240 }}>
              <label>Password (leave blank to keep unchanged)</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
            </div>
            <div className="field" style={{ width: 200 }}>
              <label>&nbsp;</label>
              <button type="button" className="btn" onClick={() => setPassword(genPassword())}>Generate</button>
            </div>
          </div>
          <div className="field" style={{ marginTop: 12 }}>
            <label>Site access</label>
            <div style={{ maxHeight: 180, overflow: "auto", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, padding: 8 }}>
              {domains.map((d) => (
                <label key={d} className="filterChip" style={{ display: "block", marginBottom: 4 }}>
                  <input type="checkbox" checked={allowedSites.includes(d)} onChange={() => toggleSite(d)} /> {d}
                </label>
              ))}
            </div>
            <p className="muted" style={{ marginTop: 6 }}>Administrators automatically have access to all sites.</p>
          </div>
          {err ? <p className="err">{err}</p> : null}
        </div>
        <div className="modalActions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" disabled={saving} onClick={() => void handleSave()}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
